/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `useFieldValidation` for the Caintra Connect frontend.
 * @module hooks/useFieldValidation
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { validateField, getValidationState } from "../utils/validation";

export const useFieldValidation = (initialValues = {}) => {
  const { t } = useTranslation();
  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldValues, setFieldValues] = useState(initialValues);

  // Get error message for a field
  const getErrorMessage = useCallback(
    (fieldName) => {
      const error = fieldErrors[fieldName];
      if (!error) {
        return null;
      }

      switch (error.error) {
        case "FIELD_REQUIRED":
          return t("errors.validation.FIELD_REQUIRED");
        case "FIELD_TOO_LONG":
          return t("errors.validation.FIELD_TOO_LONG", {
            maxLength: error.maxLength,
            currentLength: error.currentLength,
          });
        case "FIELD_TOO_SHORT":
          return t("errors.validation.FIELD_TOO_SHORT", {
            minLength: error.minLength,
            currentLength: error.currentLength,
          });
        case "FIELD_INVALID_PATTERN":
          return t(`errors.validation.${error.patternDescription}`);
        case "INVALID_DATE":
          return t("errors.validation.INVALID_DATE");
        case "FUTURE_DATE_NOT_ALLOWED":
          return t("errors.validation.FUTURE_DATE_NOT_ALLOWED");
        case "MINIMUM_AGE_REQUIRED":
          return t("errors.validation.MINIMUM_AGE_REQUIRED");
        case "MAXIMUM_AGE_EXCEEDED":
          return t("errors.validation.MAXIMUM_AGE_EXCEEDED");
        case "PHONE_MUST_BE_10_DIGITS":
          return t("errors.validation.PHONE_MUST_BE_10_DIGITS");
        case "PHONE_EXTENSION_MAX_5_DIGITS":
          return t("errors.validation.PHONE_EXTENSION_MAX_5_DIGITS");
        case "INVALID_EXPERIENCE_FORMAT":
          return t("errors.validation.INVALID_EXPERIENCE_FORMAT");
        case "EXPERIENCE_TOO_HIGH":
          return t("errors.validation.EXPERIENCE_TOO_HIGH");
        case "EXPERIENCE_CANNOT_BE_NEGATIVE":
          return t("errors.validation.EXPERIENCE_CANNOT_BE_NEGATIVE");
        case "NEGATIVE_NUMBERS_NOT_ALLOWED":
          return t("errors.validation.NEGATIVE_NUMBERS_NOT_ALLOWED");
        case "INVALID_FORMAT_OR_SYMBOLS":
          return t("errors.validation.INVALID_FORMAT_OR_SYMBOLS");
        case "DECIMAL_NUMBERS_NOT_ALLOWED":
          return t("errors.validation.DECIMAL_NUMBERS_NOT_ALLOWED");
        default:
          return t("errors.validation.FIELD_REQUIRED");
      }
    },
    [fieldErrors, t],
  );

  // Get validation state for UI styling
  const getFieldValidationState = useCallback((fieldName, value) => {
    return getValidationState(value, fieldName);
  }, []);

  // Handle field change with validation
  const handleFieldChange = useCallback(
    (fieldName, value, customRules = null) => {
      setFieldValues((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      const validation = validateField(value, fieldName, customRules);

      if (!validation.isValid) {
        setFieldErrors((prev) => ({
          ...prev,
          [fieldName]: validation,
        }));
      } else {
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    },
    [],
  );

  // Validate all fields at once
  const validateAllFields = useCallback(
    (fieldNames, customRules = {}) => {
      const errors = {};
      let hasErrors = false;

      fieldNames.forEach((fieldName) => {
        const value = fieldValues[fieldName];
        const rules = customRules[fieldName];
        const validation = validateField(value, fieldName, rules);

        if (!validation.isValid) {
          errors[fieldName] = validation;
          hasErrors = true;
        }
      });

      setFieldErrors(errors);
      return !hasErrors;
    },
    [fieldValues],
  );

  // Check if form has any errors
  const hasErrors = Object.keys(fieldErrors).length > 0;

  return {
    fieldValues,
    fieldErrors,
    hasErrors,
    validateAllFields,
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
  };
};
